"use client"

import { useEffect, useState } from "react"
import { useParams, useNavigate, Link } from "react-router-dom"
import DashboardLayout from "../components/Layout/DashboardLayout"
import Breadcrumbs from "../components/Layout/Breadcrumbs"
import MiniFooter from "../components/Layout/miniFooter"
import { Card, Button, Spinner, Form } from "react-bootstrap"
import api from "../services/api"

export default function EditLinkPage() {
  const { id } = useParams()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")

  const [form, setForm] = useState({
    originalUrl: "",
    password: "",
    expiresAt: "",
  })

  useEffect(() => {
    const fetchLink = async () => {
      try {
        const res = await api.get(`/links/${id}`)
        const link = res.data.data

        setForm({
          originalUrl: link.originalUrl || "",
          password: "",
          expiresAt: link.expiresAt ? new Date(link.expiresAt).toISOString().slice(0, 16) : "",
        })
      } catch (err) {
        setError("Failed to load link")
      } finally {
        setLoading(false)
      }
    }

    fetchLink()
  }, [id])

  const handleChange = (e) => {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError("")
    setSuccess("")

    try {
      await api.put(`/links/edit/${id}`, {
        originalUrl: form.originalUrl,
        password: form.password || undefined,
        expiresAt: form.expiresAt || null,
      })

      setSuccess("Link updated successfully")
      setTimeout(() => navigate("/dashboard/urls"), 1200)
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update link")
    } finally {
      setSaving(false)
    }
  }

  const breadcrumbItems = [
    { name: "Dashboard", path: "/user-dashboard" },
    { name: "Manage URLs", path: "/dashboard/urls" },
    { name: "Edit Link" },
  ]

  // Shared styles
  const styles = {
    pageContainer: {
      maxWidth: 740,
      margin: "0 auto",
      padding: "32px 16px",
    },
    header: {
      marginBottom: 32,
    },
    title: {
      fontSize: 24,
      fontWeight: 700,
      color: "#1e293b",
      marginBottom: 6,
    },
    subtitle: {
      fontSize: 14,
      color: "#64748b",
      margin: 0,
    },
    card: {
      border: "1px solid #e2e8f0",
      borderRadius: 16,
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
      overflow: "hidden",
    },
    cardBody: {
      padding: 32,
    },
    sectionTitle: {
      fontSize: 11,
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      color: "#94a3b8",
      marginBottom: 20,
    },
    formGroup: {
      marginBottom: 24,
    },
    label: {
      fontSize: 14,
      fontWeight: 600,
      color: "#334155",
      marginBottom: 8,
      display: "flex",
      alignItems: "center",
      gap: 8,
    },
    optionalBadge: {
      fontSize: 10,
      fontWeight: 500,
      color: "#94a3b8",
      background: "#f1f5f9",
      padding: "2px 8px",
      borderRadius: 4,
    },
    input: {
      height: 48,
      borderRadius: 10,
      border: "1px solid #e2e8f0",
      fontSize: 14,
      padding: "0 16px",
      transition: "all 0.2s ease",
      backgroundColor: "#fafbfc",
    },
    inputFocus: {
      borderColor: "#3b82f6",
      boxShadow: "0 0 0 3px rgba(59,130,246,0.1)",
      backgroundColor: "#fff",
    },
    helpText: {
      fontSize: 12,
      color: "#94a3b8",
      marginTop: 8,
    },
    iconWrapper: {
      width: 36,
      height: 36,
      borderRadius: 10,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginRight: 12,
    },
    divider: {
      height: 1,
      background: "#f1f5f9",
      margin: "28px 0",
    },
    alertSuccess: {
      background: "#f0fdf4",
      border: "1px solid #bbf7d0",
      color: "#166534",
      borderRadius: 12,
      padding: "14px 18px",
      fontSize: 14,
      fontWeight: 500,
      display: "flex",
      alignItems: "center",
      gap: 10,
    },
    alertError: {
      background: "#fef2f2",
      border: "1px solid #fecaca",
      color: "#991b1b",
      borderRadius: 12,
      padding: "14px 18px",
      fontSize: 14,
      fontWeight: 500,
      display: "flex",
      alignItems: "center",
      gap: 10,
    },
    footer: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      paddingTop: 24,
      borderTop: "1px solid #f1f5f9",
      marginTop: 8,
    },
    cancelBtn: {
      background: "transparent",
      border: "1px solid #e2e8f0",
      color: "#64748b",
      fontWeight: 500,
      fontSize: 14,
      padding: "10px 20px",
      borderRadius: 10,
      transition: "all 0.2s ease",
    },
    saveBtn: {
      background: "#3b82f6",
      border: "none",
      color: "#fff",
      fontWeight: 600,
      fontSize: 14,
      padding: "12px 28px",
      borderRadius: 10,
      display: "flex",
      alignItems: "center",
      gap: 8,
      transition: "all 0.2s ease",
    },
    saveBtnDisabled: {
      background: "#93c5fd",
      cursor: "not-allowed",
    },
    loadingContainer: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "80px 0",
      gap: 16,
    },
    loadingText: {
      fontSize: 14,
      color: "#64748b",
    },
  }

  // Icons
  const LinkIcon = () => (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  )

  const LockIcon = () => (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  )

  const ClockIcon = () => (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  )

  const CheckIcon = () => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  )

  const AlertIcon = () => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" x2="12" y1="8" y2="12" />
      <line x1="12" x2="12.01" y1="16" y2="16" />
    </svg>
  )

  const ArrowLeftIcon = () => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m12 19-7-7 7-7" />
      <path d="M19 12H5" />
    </svg>
  )

  const SaveIcon = () => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
      <polyline points="17 21 17 13 7 13 7 21" />
      <polyline points="7 3 7 8 15 8" />
    </svg>
  )

  if (loading) {
    return (
      <DashboardLayout>
        <div style={styles.pageContainer}>
          <div style={styles.loadingContainer}>
            <Spinner animation="border" style={{ color: "#3b82f6", width: 40, height: 40 }} />
            <span style={styles.loadingText}>Loading link details...</span>
          </div>
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <div style={styles.pageContainer}>
        <Breadcrumbs items={breadcrumbItems} />

        <div style={styles.header}>
          <h1 style={styles.title}>Edit Short Link</h1>
          <p style={styles.subtitle}>Update destination URL, password protection, or expiration settings</p>
        </div>

        {/* Alerts */}
        {error && (
          <div style={{ ...styles.alertError, marginBottom: 24 }}>
            <AlertIcon />
            {error}
          </div>
        )}
        {success && (
          <div style={{ ...styles.alertSuccess, marginBottom: 24 }}>
            <CheckIcon />
            {success}
          </div>
        )}

        <Card style={styles.card}>
          <Card.Body style={styles.cardBody}>
            <Form onSubmit={handleSubmit}>
              {/* Destination URL Section */}
              <div style={styles.sectionTitle}>Destination</div>

              <Form.Group style={styles.formGroup}>
                <Form.Label style={styles.label}>
                  <span style={{ ...styles.iconWrapper, background: "#eff6ff", color: "#3b82f6" }}>
                    <LinkIcon />
                  </span>
                  Destination URL
                </Form.Label>
                <Form.Control
                  type="text"
                  name="originalUrl"
                  value={form.originalUrl}
                  onChange={handleChange}
                  placeholder="https://example.com/your-page"
                  required
                  style={styles.input}
                />
                <div style={styles.helpText}>
                  This is where users will be redirected when they click your short link
                </div>
              </Form.Group>

              <div style={styles.divider} />

              {/* Security Section */}
              <div style={styles.sectionTitle}>Security & Expiration</div>

              <Form.Group style={styles.formGroup}>
                <Form.Label style={styles.label}>
                  <span style={{ ...styles.iconWrapper, background: "#fef3c7", color: "#d97706" }}>
                    <LockIcon />
                  </span>
                  Password Protection
                  <span style={styles.optionalBadge}>Optional</span>
                </Form.Label>
                <Form.Control
                  type="password"
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  placeholder="Enter new password or leave blank to remove"
                  style={styles.input}
                />
                <div style={styles.helpText}>Users will need to enter this password before being redirected</div>
              </Form.Group>

              <Form.Group style={styles.formGroup}>
                <Form.Label style={styles.label}>
                  <span style={{ ...styles.iconWrapper, background: "#f3e8ff", color: "#9333ea" }}>
                    <ClockIcon />
                  </span>
                  Expiration Date
                  <span style={styles.optionalBadge}>Optional</span>
                </Form.Label>
                <Form.Control
                  type="datetime-local"
                  name="expiresAt"
                  value={form.expiresAt}
                  onChange={handleChange}
                  style={styles.input}
                />
                <div style={styles.helpText}>After this date, the link will become inactive and return a 404 error</div>
              </Form.Group>

              {/* Actions */}
              <div style={styles.footer}>
                <Link to="/dashboard/urls" style={{ textDecoration: "none" }}>
                  <Button
                    variant="light"
                    style={styles.cancelBtn}
                    onMouseEnter={(e) => {
                      e.target.style.background = "#f8fafc"
                      e.target.style.borderColor = "#cbd5e1"
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = "transparent"
                      e.target.style.borderColor = "#e2e8f0"
                    }}
                  >
                    <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <ArrowLeftIcon />
                      Cancel
                    </span>
                  </Button>
                </Link>

                <Button
                  type="submit"
                  disabled={saving}
                  style={{
                    ...styles.saveBtn,
                    ...(saving ? styles.saveBtnDisabled : {}),
                  }}
                  onMouseEnter={(e) => {
                    if (!saving) e.target.style.background = "#2563eb"
                  }}
                  onMouseLeave={(e) => {
                    if (!saving) e.target.style.background = "#3b82f6"
                  }}
                >
                  {saving ? (
                    <>
                      <Spinner size="sm" animation="border" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <SaveIcon />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </div>

      <MiniFooter />
    </DashboardLayout>
  )
}
